import React, { useState } from "react";
import { axiosNewInstance } from "../../config/Api";
import LangData from "../common/LangData";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const PBLBusiness = () => {
    const token = localStorage.getItem("authToken");

    // Default dates (e.g., current year)
    const [startDt, setStartDt] = useState(new Date(new Date().getFullYear(), 0, 1));
    const [endDt, setEndDt] = useState(new Date());

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const formatDate = (date) => {
        if (!date) return "";
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    };

    const fetchPBLBusiness = async () => {
        if (!token) {
            setError("Authentication token missing. Please log in again.");
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        const sDt = formatDate(startDt);
        const eDt = formatDate(endDt);

        const payload = {
            startDt: sDt,
            endDt: eDt
        };

        try {
            const response = await axiosNewInstance.post("api/pblBusinessEmployee", payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "application/json",
                },
            });

            if (response.data) {
                setResult(response.data);
            } else {
                setError("No data found for the selected criteria.");
            }
        } catch (err) {
            console.error("API Error:", err);
            const errMsg = err.response?.data?.message || err.response?.data?.Error || "Failed to fetch Premier Bank business data. Please check your connection or try again later.";
            setError(errMsg);
        } finally {
            setLoading(false);
        }
    };

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const renderResult = () => {
        if (!result) return null;

        // Ensure result is an array
        let data = Array.isArray(result) ? [...result] : [result];

        if (data.length === 0 || typeof data[0] !== 'object' || data[0] === null) {
            return <div className="alert alert-info mt-3">No records found.</div>;
        }

        const columns = Object.keys(data[0]);

        // Sorting logic
        if (sortConfig.key !== null) {
            data.sort((a, b) => {
                const aVal = a[sortConfig.key];
                const bVal = b[sortConfig.key];

                if (aVal === bVal) return 0;

                // Handle null/undefined values
                if (aVal === null || aVal === undefined) return 1;
                if (bVal === null || bVal === undefined) return -1;

                // Numeric comparison if possible
                const aNum = parseFloat(aVal);
                const bNum = parseFloat(bVal);

                const isNumeric = (v) => v !== null && v !== "" && !isNaN(v);

                if (isNumeric(aVal) && isNumeric(bVal)) {
                    return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
                }

                // String comparison
                return sortConfig.direction === 'asc'
                    ? String(aVal).localeCompare(String(bVal))
                    : String(bVal).localeCompare(String(aVal));
            });
        }

        // Calculation logic for totals
        const totals = {};
        columns.forEach((col) => {
            // Skip summation for slno or other id-like columns
            const lowerCol = col.toLowerCase();
            if (lowerCol === 'slno' || lowerCol === 'id' || lowerCol === 'serial' || lowerCol === 'userid') {
                totals[col] = "";
                return;
            }

            let isNumeric = false;
            let sum = 0;
            data.forEach((row) => {
                const val = row[col];
                if (val !== null && val !== undefined && val !== "") {
                    const num = parseFloat(val);
                    if (!isNaN(num)) {
                        isNumeric = true;
                        sum += num;
                    }
                }
            });
            if (isNumeric) {
                // If it's an integer, return as integer, else 2 decimal places
                totals[col] = sum % 1 === 0 ? sum.toString() : sum.toFixed(2);
            } else {
                totals[col] = "";
            }
        });

        return (
            <div className="table-responsive mt-3" style={{ maxHeight: "650px", overflowY: "auto" }}>
                <table className="table table-sm text-left">
                    <thead>
                        <tr>
                            <th>#</th>
                            {columns.map((col) => (
                                <th
                                    key={col}
                                    onClick={() => requestSort(col)}
                                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                                    className={sortConfig.key === col ? "bg-primary text-white" : ""}
                                >
                                    {col} {sortConfig.key === col ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, index) => (
                            <tr key={index}>
                                <td>{index + 1}</td>
                                {columns.map((col) => (
                                    <td key={col} className="text-nowrap">
                                        {typeof row[col] === "object" && row[col] !== null
                                            ? JSON.stringify(row[col])
                                            : String(row[col] ?? "")}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                    <tfoot style={{ position: "sticky", bottom: 0, backgroundColor: "#f1f3f5", fontWeight: "bold", borderTop: "2px solid #dee2e6" }}>
                        <tr>
                            <td style={{ backgroundColor: "#f1f3f5" }}>Total</td>
                            {columns.map((col) => (
                                <td key={col} className="text-nowrap" style={{ backgroundColor: "#f1f3f5" }}>
                                    {totals[col]}
                                </td>
                            ))}
                        </tr>
                    </tfoot>
                </table>
            </div>
        );
    };

    return (
        <div className="container">
            <div className="row g-0">
                <div className="col-md-12 pr-2 mt-4">
                    <div className="card p-3 shadow-sm">
                        <h5 className="font-weight-bold fs-4 my-3 text-left">
                            <LangData keyname="pbl_business" />
                        </h5>

                        <div className="row mb-3 align-items-end p-2 bg-light rounded shadow-sm mx-1">
                            <div className="col-md-3">
                                <label className="font-weight-bold small">Start Date</label>
                                <DatePicker
                                    selected={startDt}
                                    onChange={(date) => setStartDt(date)}
                                    dateFormat="yyyy-MM-dd"
                                    className="form-control"
                                />
                            </div>
                            <div className="col-md-3">
                                <label className="font-weight-bold small">End Date</label>
                                <DatePicker
                                    selected={endDt}
                                    onChange={(date) => setEndDt(date)}
                                    dateFormat="yyyy-MM-dd"
                                    className="form-control"
                                />
                            </div>
                            <div className="col-md-2">
                                <button
                                    className="btn btn-primary btn-block"
                                    onClick={fetchPBLBusiness}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm mr-1" />
                                            Searching...
                                        </>
                                    ) : (
                                        "Search"
                                    )}
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div className="alert alert-danger mt-3 py-2">
                                {error}
                            </div>
                        )}

                        {renderResult()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PBLBusiness;
